import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { RootState, UUID } from '@umanforge/common';

import InputText from '@common_components/inputs/textInput.component';

import { SectorSetting } from 'interfaces/sectorSetting.interface';

import { SectorSettingsService } from 'services/sectorSettings.service';

import { SECTOR_SETTING_KEY } from 'constants/sectorSetting.constants';

interface SettingsSectorProps {
  key: string;
  selectedSectorId: UUID;
}


const SettingsSector = ( { selectedSectorId }: SettingsSectorProps ) => {

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const token = useSelector( ( state: RootState ) => {return state.token.value;} );

  const [ apiSectorSettings, setApiSectorSettings ] = useState<SectorSetting[]>( [] );
  const [ changedSectorSettings, setChangedSectorSettings ] = useState<SectorSetting[]>( [] );
  const [ switchTimeMs, setSwitchTimeMs ] = useState<string | null>( null );

  const [ isSaving, setIsSaving ] = useState<boolean>( false );
  const [ canSave, setCanSave ] = useState<boolean>( false );

  const mapSetter = useMemo( () => {
    const map = new Map<string, React.Dispatch<React.SetStateAction<string | null>>>();
    map.set( SECTOR_SETTING_KEY.SWITCH_TIME, setSwitchTimeMs );
    return map;
  }, [] );


  useEffect( () => {
    refreshSettings( selectedSectorId );
  }, [ selectedSectorId ] );

  useEffect( () => {
    checkCanSave();
  }, [ changedSectorSettings ] );

  const refreshSettings = async ( sectorId: UUID ) => {
    const updatedSettings = await SectorSettingsService.getSectorSettings( sectorId, token, dispatch );
    setChangedSectorSettings( [] );
    setApiSectorSettings( updatedSettings );
    for ( const setting of updatedSettings ) {
      const setter = mapSetter.get( setting.keyframe );
      if ( setter ) {
        setter( setting.value );
      }
    }
  };

  const onSettingChanged = ( keyframe: string, newValue: string ) => {
    const findedSetting = apiSectorSettings.find( setting => { return setting.keyframe === keyframe; } );
    if ( findedSetting ) {
      const value = findedSetting.value;
      const formattedNewValue = newValue;
      const changedSectorWithoutCurrSetting = changedSectorSettings.filter( setting => { return setting.keyframe != keyframe; } );
      if ( value === formattedNewValue ) {
        setChangedSectorSettings( changedSectorWithoutCurrSetting );
      } else {
        setChangedSectorSettings( [ ...changedSectorWithoutCurrSetting, { ...findedSetting, value: formattedNewValue } ] );
      }
      const setter = mapSetter.get( keyframe );
      if ( setter ) {
        setter( formattedNewValue );
      }
    }
  };

  const checkCanSave = () => {
    const hasChanged = changedSectorSettings.length > 0;
    let isCorrect = true;

    if ( hasChanged ) {
      const switchTime = Number( changedSectorSettings.find( setting => { return setting.keyframe === SECTOR_SETTING_KEY.SWITCH_TIME; } )?.value );
      if ( !Number.isNaN( switchTime ) ) {
        const formattedValue = Number( switchTime );
        isCorrect = (
          isCorrect &&
          formattedValue >= 5000
        );
      }
    }

    setCanSave( hasChanged && isCorrect );
  };

  const handleSave = async () => {
    if ( canSave && !isSaving ) {
      setIsSaving( true );
      try {
        const promises: Promise<void>[] = [];

        for ( const setting of changedSectorSettings ) {
          promises.push( SectorSettingsService.updateSectorSettingById( setting.id, setting.value, token, dispatch ) );
        }
        await Promise.all( promises );
        await refreshSettings( selectedSectorId );
      } finally {
        setIsSaving( false );
      }
    }
  };

  const convertStringMsToS = ( value: string ): string => {
    let convertedValue = Number( value );
    if ( Number.isNaN( convertedValue ) ) {
      convertedValue = 0;
    }
    return String( convertedValue / 1000 );
  };

  const convertStringSToMs = ( value: string ): string => {
    let convertedValue = Number( value );
    if ( Number.isNaN( convertedValue ) ) {
      convertedValue = 0;
    }
    return String( convertedValue * 1000 );
  };

  return (
    <div className='flex flex-col gap-6'>

      <div className='flex flex-col'>

        <div className="p-6 rounded-xl border bg-white">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">
            { t( 'translation:global_visualisation' ) }
          </h2>

          <div className="flex flex-col gap-4">
            { /* Partie Temps d'affichage des tableaux en visualisation global */ }
            {
              switchTimeMs != null && (
                <InputText
                  label={ `${t( 'translation:settings.' + SECTOR_SETTING_KEY.SWITCH_TIME )} (${t( 'common:in_seconds' )})` }
                  type='number'
                  textSize='w-5/6'
                  min={ 5 }
                  isRequired
                  value={ convertStringMsToS( switchTimeMs ) }
                  onChange={( value: string ) => {
                    return onSettingChanged( SECTOR_SETTING_KEY.SWITCH_TIME, convertStringSToMs( value ) );
                  }
                  }
                />
              )
            }
          </div>
        </div>

      </div>

      <div className='flex justify-end'>
        <button
          className='mt-6 mb-24 px-4 py-2 bg-blue-100 text-blue-600 rounded disabled:opacity-50 hover:bg-blue-200 trans-fast'
          onClick={handleSave}
          disabled={isSaving || !canSave}
        >
          {isSaving ? t( 'common:saving' ) : t( 'common:save' )}
        </button>
      </div>
    </div>
  );
};

export default SettingsSector;
